import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  ActionItemElement,
  FileUploaderV2,
  RotecFormGroupConfigV2,
  RotecFormOptionsV2,
  SelectItemV2,
  deepClone,
  FormsModuleV2,
  SelectV2Module,
} from '@schenck/rotec-components';
import {
  KIND_OF_TASK_ITEMS,
  PRIORITY_ITEMS,
  RESPONSIBLE_COMPANY_ITEMS,
} from 'src/utils/constants/upsert-task.const';
import { ToolingTaskUpsertModel } from '../../upsert-tooling-task-wizard.component';
import { ToolingStepBaseComponent } from '../tooling-step-base.component';
import { DurationUnit, DurationUnitNames } from 'src/utils/constants/duration.const';

@Component({
  selector: 'ma-tooling-description-step',
  templateUrl: './description-step.component.html',
  styleUrls: ['./description-step.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModuleV2, SelectV2Module],
})
export class ToolingDescriptionStepComponent extends ToolingStepBaseComponent implements OnInit {
  public readonly durationMinutesLabel =
    viewChild<TemplateRef<HTMLElement>>('durationMinutesLabel');
  public readonly actionSelect = viewChild<ActionItemElement>('actionSelect');
  @Input() public fileUploader: FileUploaderV2;

  public descriptionFields: RotecFormGroupConfigV2<ToolingTaskUpsertModel>;

  public descriptionFormOptions: RotecFormOptionsV2 = {
    formId: 'descriptionFormMachineTask',
    props: { labelMinWidth: '250px' },
  };

  public durationUnits: SelectItemV2[] = [
    { name: DurationUnitNames[DurationUnit.MIN], value: DurationUnit.MIN },
    { name: DurationUnitNames[DurationUnit.H], value: DurationUnit.H },
  ];

  public toggleUnitSelect(): void {
    this.actionSelect()?.toggleSelect(null, true);
  }

  public durationUnit: DurationUnit = DurationUnit.MIN;

  public changeDurationUnit({ value }: SelectItemV2): void {
    this.durationUnit = value as DurationUnit;
    this.toolingTaskModel.expectedDurationUnit = this.durationUnit;

    const expectedDurationValue = this.stepForm.get('expectedDurationMinutes').value;
    if (value === DurationUnit.H) {
      // convert minutes to hours
      this.stepForm.get('expectedDurationMinutes').setValue(expectedDurationValue / 60);
    } else {
      // convert hours to minutes
      this.stepForm.get('expectedDurationMinutes').setValue(expectedDurationValue * 60);
    }
  }

  public ngOnInit(): void {
    this.setupDescriptionForm();
  }

  private setupDescriptionForm(): void {
    this.descriptionFields = [
      { key: 'id' },
      {
        type: 'file-upload-v2',
        key: 'pictureIds',
        props: {
          label: 'Image, Add or Drag and Drop Images',
          service: this.fileUploader,
          acceptedFileFormats: ['.svg', '.png', '.jpg', '.jpeg'],
          fallbackImageSrc: './assets/img/favicon.ico',
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'title',
        props: {
          label: 'Title',
          placeholder: '-',
          required: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'schenckId',
        props: {
          label: 'Task ID Schenck',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'customerId',
        props: {
          label: 'Task ID Customer',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'textarea-v2',
        key: 'description',
        props: {
          label: 'Description',
          placeholder: '-',
          minHeight: '100px',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'notice',
        props: {
          label: 'Notice',
          placeholder: '-',
          darkMode: true,
        },
      },
      {
        type: 'select-v2',
        key: 'priority',
        props: {
          label: 'Priority',
          translateNames: true,
          placeholder: 'Select priority',
          required: true,
          items: deepClone(PRIORITY_ITEMS),
          darkMode: true,
        },
      },
      {
        type: 'select-v2',
        key: 'type',
        props: {
          label: 'Task Type',
          translateNames: true,
          placeholder: 'Select task type',
          items: deepClone(KIND_OF_TASK_ITEMS),
          darkMode: true,
          secondary: true,
        },
      },
      {
        type: 'select-v2',
        key: 'responsibleCompany',
        props: {
          label: 'Responsible Company',
          translateNames: true,
          placeholder: 'Select responsible company',
          items: deepClone(RESPONSIBLE_COMPANY_ITEMS),
          required: true,
          darkMode: true,
        },
        defaultValue: 'Customer',
      },
      {
        type: 'input-v2',
        key: 'responsible',
        props: {
          label: 'Task Responsible',
          placeholder: '-',
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'executive',
        props: {
          label: 'Task Executive',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'number-input-v2',
        key: 'expectedDurationMinutes',
        props: {
          label: 'Expected Duration',
          afterLabelTemplate: this.durationMinutesLabel(),
          min: 0,
          isAdditionalField: true,
          darkMode: true,
        },
        expressionProperties: {
          'props.placeholder': () => {
            return DurationUnitNames[this.durationUnit];
          },
        },
        validators: {
          minute: {
            expression: (control: AbstractControl) =>
              !control.value || !isNaN(parseInt(control.value, 10)),
            message: 'Enter a valid number',
          },
        },
      },
      {
        type: 'textarea-v2',
        key: 'notes',
        props: {
          label: 'Notes',
          placeholder: '-',
          minHeight: '200px',
          darkMode: true,
        },
      },
    ];
  }
}
